import * as React from 'react';
import * as Images from 'pos/Images';
import { z } from 'zod';
import { Res, LanguageDTO, PosInListDTO } from 'core/api';
import { Result } from 'core/infrastructure';
import { Cordova, Settings } from 'pos/common';
import { ThemeManager, PosScreen, Header, Footer, PhoneOnly, LargeDeviceOnly, Filler, ActionButton, BackActionButton, Content } from 'pos/ui';
import showLog from './showLog';
import { RestaurantFacade } from 'pos/facades';
import { SettingsProvider } from 'pos/services';
import { Message, Modal } from 'core/ui-next/modal';
import { BasicButton, SvgIcon } from 'core/ui-next';
import { Box, VStack, Text, Link } from '@chakra-ui/react';
import { FormRef, useControlsFor } from 'core/ui-next/controls';

type Props = {
	languages: LanguageDTO[];
	allPos: PosInListDTO[];
	themes: ThemeManager.Theme[];
	settings: Settings;
} & Modal.Actions<Settings>;

const languageDTOSchema = z.object({
	Code: z.string(),
	Name: z.string().optional(),
});

const posInListDTOSchema = z.object({
	Id: z.number(),
	Name: z.string().optional(),
});

const selectedThemeSchema = z.object({
	name: z.string(),
	value: z.string()
});

const stateSchema = z.object({
	selectedLanguage: languageDTOSchema.nullable(),
	selectedPos: posInListDTOSchema.nullable(),
	selectedTheme: selectedThemeSchema,
	server: z.string(),
});

type PosSettings = z.infer<typeof stateSchema>;

function ConfigureScreen(props: Props) {
	const { close, cancel, settings } = props;
	const onCancel = React.useCallback(async () => cancel(), [cancel]);
	const onAccept = React.useCallback(async () => {
		const result = await fromRef.current!.getValue();
		if (!result.ok)
			return;

		close({
			...settings,
			posId: result.value.selectedPos!.Id,
			languageCode: result.value.selectedLanguage!.Code,
			theme: result.value.selectedTheme.value
		});

	}, [close, settings]);

	const showSettings = async (): Promise<void> => {

		const result = await Message.confirm(Res.Web.POS_CONFIG_CONFIRM_GO_BACK_TO_WEB_CONFIGURATION_LBL());
		if (result.ok) {
			await Cordova.showSettings();
		}
	};

	const { Form, Select } = useControlsFor<PosSettings>();
	const fromRef = React.useRef<FormRef<PosSettings>>(null);

	const Icon = React.useMemo(() => Images.Config, []);

	const initialValue: PosSettings = {
		selectedLanguage: props.languages.find(x => x.Code === settings.languageCode) || props.languages[0],
		selectedPos: props.allPos.find(x => x.Id === settings.posId) || props.allPos[0],
		selectedTheme: props.themes.find(x => x.value === settings.theme) || props.themes[0],
		server: document.location.href
	};

	return <PosScreen screenId='CONFIGURE' size={'medium'} >
		<Header title={Res.Web.POS_CONFIG()} onClose={onCancel} />

		<Content>
			<VStack w='full' gap='6' p={'6'} align={'center'} overflowY={'auto'} height={'full'} >
				<VStack gap='4' align={'center'}>
					<SvgIcon src={Icon} h='12' w='12' />
					<Text fontWeight={'bold'} fontSize={'md'}>{Res.Web.POS_CONFIG_TITLE()}</Text>
				</VStack>

				<VStack p={'4'} w='full' maxWidth={'96'} gap={'4'} bg={{ base: '{colors.neutral.100}', _dark: '{colors.neutral.800}' }} rounded={'lg'} shadow={'md'} blur={'md'}>
					<Box w='full'>
						<Form initialValue={initialValue} schema={stateSchema} ref={fromRef}>
							<VStack w='full' gap='4'>
								{Cordova.isCordovaNavigator()
									? <Text onClick={showSettings}>{Res.Web.POS_CONFIG_SERVER_LBL()}<br />
										<Link variant='underline'>{initialValue.server}</Link>
									</Text>
									: null}
								<Select label={Res.Web.POS_CONFIG_POS_LBL()} linkTo={'selectedPos'} options={props.allPos} renderOption={opt => opt?.Name ?? ''} multiple={false} placeholder={Res.Web.POS_CONFIG_POS_PLACEHOLDER()} valueGetter={opt => opt?.Name ?? ''} variant={'borderTransparent'} required />
								<Select label={Res.Web.POS_CONFIG_LANGUAGE_LBL()} linkTo={'selectedLanguage'} options={props.languages} renderOption={opt => opt?.Name ?? ''} multiple={false} placeholder={Res.Web.POS_CONFIG_LANGUAGE_PLACEHOLDER()} valueGetter={opt => opt?.Name ?? ''} variant={'borderTransparent'} required />
								<Select label={Res.Web.POS_CONFIG_THEME_LBL()} linkTo={'selectedTheme'} options={props.themes} renderOption={opt => opt?.name ?? ''} multiple={false} placeholder={Res.Web.POS_CONFIG_THEME_PLACEHOLDER()} valueGetter={opt => opt?.name ?? ''} variant={'borderTransparent'} required />
							</VStack>
						</Form>
					</Box>
				</VStack>
				<BasicButton type='success' maxWidth={'96'} text={Res.Common.OK_BTN()} onClick={onAccept} />
			</VStack>
		</Content>


		<Footer>
			<PhoneOnly>
				<BackActionButton action={onCancel} />
				<Filler />
				<ActionButton type={'primary'} image={Images.Log} action={showLog} />
			</PhoneOnly>

			<LargeDeviceOnly>
				<Filler />
				<ActionButton type={'primary'} image={Images.Log} action={showLog} />
			</LargeDeviceOnly>
		</Footer>

	</PosScreen>;
}

export default async function configureSettings(): Promise<Result> {

	const [allPos, allLanguages] = await Promise.all([RestaurantFacade.getAllPos(), RestaurantFacade.getLanguages()]);

	if (allPos.length === 0) {
		await Message.show(Res.Msg.MSG_NOT_AVAILABLE_POS());
		return Result.error();
	}

	const settings = SettingsProvider.load();

	const result = await Modal.show(ConfigureScreen, { allPos, languages: allLanguages, themes: ThemeManager.getAllThemes(), settings });
	if (result.ok)
		SettingsProvider.save(result.value);

	return result;
}
